import { useState, useEffect, useMemo } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "../ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import {
  BookOpen,
  Calculator,
  Globe,
  Palette,
  Activity,
  Users,
  FlaskConical,
  Clock,
  MapPin,
  Coffee,
  Sun,
  Utensils,
  Music,
  MessageCircle,
  Search,
  Sparkles,
  ChevronDown,
  Check,
  Plus,
} from "lucide-react";
import { getSubjectsForNiveau, getSubjectsForCycles, createCustomSubject } from "../../lib/api";

// Icon mapping
const ICON_MAP = {
  "book-open": BookOpen,
  "calculator": Calculator,
  "globe": Globe,
  "palette": Palette,
  "activity": Activity,
  "users": Users,
  "flask-conical": FlaskConical,
  "clock": Clock,
  "map": MapPin,
  "coffee": Coffee,
  "sun": Sun,
  "utensils": Utensils,
  "music": Music,
  "message-circle": MessageCircle,
  "search": Search,
  "sparkles": Sparkles,
  "atom": FlaskConical,
  "cog": Clock,
};

export function DynamicSubjectSelector({
  value,
  onChange,
  niveau,
  cycles = [],
  placeholder = "Sélectionner une matière",
  showSubdomains = true,
  allowCustom = true,
  className = "",
}) {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [customSubject, setCustomSubject] = useState({ label: "", color: "#64748B" });

  // Fetch subjects based on niveau or cycles
  useEffect(() => {
    const fetchSubjects = async () => {
      setLoading(true);
      try {
        let response;
        if (cycles.length > 0) {
          response = await getSubjectsForCycles(cycles);
        } else if (niveau) {
          response = await getSubjectsForNiveau(niveau);
        } else {
          // Default to cycle2 if nothing specified
          response = await getSubjectsForNiveau("cp");
        }
        setSubjects(response.data.subjects || []);
      } catch (err) {
        console.error("Failed to fetch subjects:", err);
        setSubjects([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSubjects();
  }, [niveau, cycles.join(",")]);

  // Filter subjects by search
  const filteredSubjects = useMemo(() => {
    if (!search) return subjects;
    const lowerSearch = search.toLowerCase();
    return subjects.filter(s => 
      s.label.toLowerCase().includes(lowerSearch) ||
      s.code?.toLowerCase().includes(lowerSearch)
    );
  }, [subjects, search]);

  // Group subjects by type
  const groupedSubjects = useMemo(() => {
    const groups = {
      domains: filteredSubjects.filter(s => s.type === "domain" && !s.is_break && !s.is_organizational),
      subdomains: showSubdomains ? filteredSubjects.filter(s => s.type === "subdomain") : [],
      organizational: filteredSubjects.filter(s => s.is_organizational || s.type === "common"),
      breaks: filteredSubjects.filter(s => s.is_break),
      custom: filteredSubjects.filter(s => s.type === "custom"),
    };
    return groups;
  }, [filteredSubjects, showSubdomains]);

  // Get selected subject info
  const selectedSubject = useMemo(() => {
    return subjects.find(s => s.id === value || s.code === value);
  }, [subjects, value]);

  const handleSelect = (subject) => {
    onChange(subject.id, subject);
    setOpen(false);
  };

  const handleCreateCustom = async () => {
    if (!customSubject.label.trim()) return;
    
    try {
      const response = await createCustomSubject({
        label: customSubject.label,
        color: customSubject.color,
        cycle: cycles[0] || (niveau ? getNiveauCycle(niveau) : "cycle2"),
      });
      
      // Add to local list
      setSubjects(prev => [...prev, { ...response.data, type: "custom" }]);
      
      // Select it
      handleSelect(response.data);
      
      setCustomSubject({ label: "", color: "#64748B" });
      setShowCustomDialog(false);
    } catch (err) {
      console.error("Failed to create custom subject:", err);
    }
  };

  const getNiveauCycle = (niv) => {
    const map = {
      ps: "cycle1", ms: "cycle1", gs: "cycle1",
      cp: "cycle2", ce1: "cycle2", ce2: "cycle2",
      cm1: "cycle3", cm2: "cycle3", "6e": "cycle3",
    };
    return map[niv.toLowerCase()] || "cycle2";
  };

  const getIcon = (iconName) => {
    const Icon = ICON_MAP[iconName] || BookOpen;
    return Icon;
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={`justify-between ${className}`}
            disabled={loading}
          >
            {selectedSubject ? (
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: selectedSubject.color }}
                />
                <span>{selectedSubject.label}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">
                {loading ? "Chargement..." : placeholder}
              </span>
            )}
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <Command>
            <CommandInput 
              placeholder="Rechercher une matière..." 
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>
                Aucune matière trouvée.
                {allowCustom && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 w-full"
                    onClick={() => {
                      setShowCustomDialog(true);
                      setOpen(false);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Créer "{search}"
                  </Button>
                )}
              </CommandEmpty>

              {/* Domains */}
              {groupedSubjects.domains.length > 0 && (
                <CommandGroup heading="Domaines">
                  {groupedSubjects.domains.map((subject) => {
                    const Icon = getIcon(subject.icon);
                    return (
                      <CommandItem
                        key={subject.id}
                        value={subject.label}
                        onSelect={() => handleSelect(subject)}
                        className="cursor-pointer"
                      >
                        <div 
                          className="w-3 h-3 rounded-full mr-2" 
                          style={{ backgroundColor: subject.color }}
                        />
                        <Icon className="w-4 h-4 mr-2" style={{ color: subject.color }} />
                        <span>{subject.label}</span>
                        {value === subject.id && (
                          <Check className="ml-auto h-4 w-4" />
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}

              {/* Subdomains */}
              {groupedSubjects.subdomains.length > 0 && (
                <CommandGroup heading="Sous-domaines">
                  {groupedSubjects.subdomains.map((subject) => (
                    <CommandItem
                      key={subject.id}
                      value={subject.label}
                      onSelect={() => handleSelect(subject)}
                      className="cursor-pointer pl-6"
                    >
                      <div 
                        className="w-2 h-2 rounded-full mr-2" 
                        style={{ backgroundColor: subject.color }}
                      />
                      <span className="text-sm">{subject.label}</span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        ({subject.parent_label})
                      </span>
                      {value === subject.id && (
                        <Check className="ml-auto h-4 w-4" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {/* Organizational */}
              {groupedSubjects.organizational.length > 0 && (
                <CommandGroup heading="Organisation">
                  {groupedSubjects.organizational.map((subject) => {
                    const Icon = getIcon(subject.icon);
                    return (
                      <CommandItem
                        key={subject.id}
                        value={subject.label}
                        onSelect={() => handleSelect(subject)}
                        className="cursor-pointer"
                      >
                        <div 
                          className="w-3 h-3 rounded-full mr-2" 
                          style={{ backgroundColor: subject.color }}
                        />
                        <Icon className="w-4 h-4 mr-2" style={{ color: subject.color }} />
                        <span>{subject.label}</span>
                        {value === subject.id && (
                          <Check className="ml-auto h-4 w-4" />
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}

              {/* Custom */}
              {groupedSubjects.custom.length > 0 && (
                <CommandGroup heading="Mes matières">
                  {groupedSubjects.custom.map((subject) => (
                    <CommandItem
                      key={subject.id}
                      value={subject.label}
                      onSelect={() => handleSelect(subject)}
                      className="cursor-pointer"
                    >
                      <div 
                        className="w-3 h-3 rounded-full mr-2" 
                        style={{ backgroundColor: subject.color }}
                      />
                      <Sparkles className="w-4 h-4 mr-2" style={{ color: subject.color }} />
                      <span>{subject.label}</span>
                      {value === subject.id && (
                        <Check className="ml-auto h-4 w-4" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {/* Add custom button */}
              {allowCustom && (
                <div className="p-2 border-t">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => {
                      setShowCustomDialog(true);
                      setOpen(false);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter une matière personnalisée
                  </Button>
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Custom Subject Dialog */}
      <Dialog open={showCustomDialog} onOpenChange={setShowCustomDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nouvelle matière personnalisée</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Nom de la matière</label>
              <Input
                value={customSubject.label}
                onChange={(e) => setCustomSubject(prev => ({ ...prev, label: e.target.value }))}
                placeholder="Ex: Éducation aux médias"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Couleur</label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="color"
                  value={customSubject.color}
                  onChange={(e) => setCustomSubject(prev => ({ ...prev, color: e.target.value }))}
                  className="w-14 h-10 p-1"
                />
                <Input
                  value={customSubject.color}
                  onChange={(e) => setCustomSubject(prev => ({ ...prev, color: e.target.value }))}
                  className="flex-1"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <div 
                className="w-4 h-4 rounded-full" 
                style={{ backgroundColor: customSubject.color }}
              />
              <span className="font-medium">{customSubject.label || "Aperçu"}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateCustom} disabled={!customSubject.label.trim()}>
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Compact badge version for display
export function SubjectBadge({ subject, className = "" }) {
  if (!subject) return null;
  
  const Icon = ICON_MAP[subject.icon] || BookOpen;
  
  return (
    <Badge 
      variant="outline" 
      className={`font-medium ${className}`}
      style={{ 
        backgroundColor: `${subject.color}15`,
        borderColor: `${subject.color}40`,
        color: subject.color
      }}
    >
      <Icon className="w-3 h-3 mr-1" />
      {subject.label}
    </Badge>
  );
}

export default DynamicSubjectSelector;
